import React, { useEffect, useState } from 'react';
import {
  TextField,
  Autocomplete,
  Box,
  Card,
  Grid,
  styled,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SaveIcon from '@mui/icons-material/Save';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Printer, PrinterProfile } from '../../../../services/apiService/response-models';
import { getPrinterList } from '../../../../services/apiService';

const EditorWrapper = styled(Box)(
  ({ theme }) => `
    .ql-editor {
      min-height: 100px;
    }
    .ql-snow .ql-picker {
      color: ${theme.colors.alpha.black[100]};
    }
    .ql-snow .ql-stroke {
      stroke: ${theme.colors.alpha.black[100]};
    }
    .ql-toolbar.ql-snow {
      border-top-left-radius: ${theme.general.borderRadius};
      border-top-right-radius: ${theme.general.borderRadius};
    }
    .ql-toolbar.ql-snow,
    .ql-container.ql-snow {
      border-color: ${theme.colors.alpha.black[30]};
    }
    .ql-container.ql-snow {
      border-bottom-left-radius: ${theme.general.borderRadius};
      border-bottom-right-radius: ${theme.general.borderRadius};
    }
    &:hover {
      .ql-toolbar.ql-snow,
      .ql-container.ql-snow {
        border-color: ${theme.colors.alpha.black[50]};
      }
    }
`
);

function GeneralSection(props: {
  model: PrinterProfile | null;
  isSaveInProgress: boolean;
  onSaveClick: () => void;
  onSave: (updatedModel: PrinterProfile) => Promise<boolean>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isEdit = Boolean(props.model?.id);
  const [printer, setprinter] = useState<Printer[]>([]);

  useEffect(() => {
    async function fetchprinter() {
      try {
        const response = await getPrinterList();
        setprinter(response.data.filter((p) => p.isActive));
      } catch (error) {
        console.error('Failed to fetch printer', error);
      }
    }
    fetchprinter();
  }, []);

  const goBack = () => {
    navigate(-1);
  };

  return (
    <Card sx={{ p: 3 }}>
      <Formik
  initialValues={{
    name: props.model?.name || '',
    description: props.model?.description || '',
    numberOfCopies: props.model?.numberOfCopies || '',
    printers: Array.isArray(props.model?.printers)
      ? props.model?.printers.map((p: any) => typeof p === 'string' ? p : p.id)
      : [props.model?.printers].filter(Boolean).map((p: any) => typeof p === 'string' ? p : p.id),
    isActive: typeof (props.model as any)?.isActive === 'boolean' ? (props.model as any).isActive : true,
  }}


        validationSchema={Yup.object().shape({
          name: Yup.string().max(250).required(t('The name field is required')),
          numberOfCopies: Yup.number()
            .typeError(t('The Number of Copies must be a number'))
            .required(t('The Number of Copies field is required'))
            .min(1, t('Number of Copies must be at least 1')),
        })}
        onSubmit={async (_values, { resetForm, setStatus, setSubmitting }) => {
          try {
            var isSuccess = await props.onSave({
              ...props.model,
              ..._values,
            });
            if (isSuccess) {
              resetForm();
              setStatus({ success: true });
              setSubmitting(false);
            }
          } catch (err) {
            console.error(err);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="name"
                  placeholder={t('Enter printer profile name...')}
                  variant="outlined"
                  label={t('Profile Name')}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.name}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  placeholder={t('Enter description...')}
                  variant="outlined"
                  label={t('Description')}
                  error={Boolean(touched.description && errors.description)}
                  helperText={touched.description && errors.description}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  value={values.description}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  name="numberOfCopies"
                  placeholder={t('Enter number...')}
                  variant="outlined"
                  label={t('Number of Copies')}
                  error={Boolean(touched.numberOfCopies && errors.numberOfCopies)}
                  helperText={touched.numberOfCopies && errors.numberOfCopies}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.numberOfCopies}
                />
              </Grid>

              {/* <Grid item xs={12}>
  <Autocomplete
    multiple
    disablePortal
    id="printers"
    options={printer}
    getOptionLabel={(option) =>
      typeof option === 'string' ? option : option.name
    }
    value={printer.filter((p) => values.printers?.includes(p.id))}
    onChange={(event, newValue) => {
      const ids = newValue.map((p) => (typeof p === 'string' ? p : p.id));
      setFieldValue('printers', ids);
    }}
    isOptionEqualToValue={(option, value) => option.id === value.id}
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        variant="outlined"
        label={t('Printers')}
        placeholder={t('Select one or more printers')}
        error={Boolean(touched.printers && errors.printers)}
        helperText={touched.printers && errors.printers}
      />
    )}
  />
</Grid> */}
<Grid item xs={12}>
  <Autocomplete
    multiple
    disablePortal
    id="printers"
    options={printer}
    getOptionLabel={(option) =>
      typeof option === 'string' ? option : option.name
    }
    value={Array.isArray(values.printers)
      ? printer.filter((p) => values.printers.includes(p.id))
      : []
    }
    onChange={(event, newValue) => {
      const ids = newValue.map((p) => (typeof p === 'string' ? p : p.id));
      setFieldValue('printers', ids);
    }}
    isOptionEqualToValue={(option, value) =>
      option.id === (typeof value === 'string' ? value : value.id)
    }
    renderInput={(params) => (
      <TextField
        {...params}
        fullWidth
        variant="outlined"
        label={t('Printers')}
        placeholder={t('Select one or more printers')}
        error={Boolean(touched.printers && errors.printers)}
        helperText={touched.printers && errors.printers}
      />
    )}
  />
</Grid>


              <Grid item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      name="isActive"
                      disabled={!isEdit}
                      color={isEdit ? 'primary' : 'secondary'}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      checked={values.isActive}
                    />
                  }
                  label={t('Is Active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Button sx={{ mr: 2 }} type="button" onClick={goBack} variant="text">
                  {t('Cancel')}
                </Button>
                <Button
                  type="submit"
                  onClick={() => props.onSaveClick()}
                  variant="contained"
                  startIcon={isSubmitting || props.isSaveInProgress ? <CircularProgress size="1rem" /> : <SaveIcon />}
                  disabled={isSubmitting || props.isSaveInProgress}
                >
                  {t('Save')}
                </Button>
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Card>
  );
}

export default GeneralSection;
